import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import useTutorialCache from "@hooks/useTutorialCache";
import { DATE_FORMAT } from "@memorang/constants/date";
import { isWeb } from "@memorang/helpers/platform";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import DateTimePicker from "@memorang/ui/components/DateTimePicker";
import { useToast } from "@memorang/ui/components/Toast";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Portal } from "react-native-paper";

const cta = "Got it";
type Props = {
	showDatePicker: boolean;
	toggleShowDatePicker: () => void;
	handleDateChange?: (date: Date) => void;
};
const SetExamDateContainer = ({
	showDatePicker,
	toggleShowDatePicker,
	handleDateChange,
}: Props) => {
	const { tutorialData, setTutorialData } = useTutorialCache();
	const { showToast } = useToast();
	const { t } = useTranslation();

	const examDateDisclaimer = useAppStore(
		(state) => state.exam.examDateDisclaimer,
	);

	const {
		currentExamContext: { examDate },
		updateExamDate,
	} = useExamContext();

	const hasSeenTutorial = tutorialData?.hasSeenExamDateTutorial;

	const handleCloseTutorial = () => {
		setTutorialData({ hasSeenExamDateTutorial: true });
	};

	if (!showDatePicker) {
		return null;
	}
	if (!hasSeenTutorial) {
		return (
			<ConfirmDialog
				visible={true}
				showCloseButton={false}
				title="Disclaimer"
				icon="alert-outline"
				body={examDateDisclaimer}
				confirmCallback={handleCloseTutorial}
				confirmCta={cta}
			/>
		);
	}

	return showDatePicker ? (
		<Portal>
			<DateTimePicker
				isVisible
				onCancel={toggleShowDatePicker}
				initialDate={examDate}
				onConfirm={(date) => {
					if (!isWeb) {
						toggleShowDatePicker();
					}
					updateExamDate(date);
					handleDateChange?.(date);
					const formattedDate = DateTime.fromJSDate(date).toFormat(DATE_FORMAT);
					showToast({
						message: t("account.examDateUpdated", { date: formattedDate }),
						duration: 1000,
					});
				}}
			/>
		</Portal>
	) : null;
};

export default SetExamDateContainer;
